import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import TournamentDetails from '@/components/TournamentDetails';
import PlayerNav from '@/components/PlayerNav';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import { createOrGetChat } from '@/hooks/useChats';
import { useToast } from '@/hooks/use-toast';

const TournamentDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('my-events');
  const [showRegistration, setShowRegistration] = useState(false);
  const { toast } = useToast();
  
  const tournamentId = id;

  // Redirect to home if no tournament ID
  useEffect(() => {
    if (!id) {
      console.error('[TournamentDetailsPage] No tournament ID provided');
      navigate('/');
    }
  }, [id, navigate]);

  useEffect(() => {
    // Check if URL has #registration hash to show registration form
    if (location.hash === '#registration') {
      setShowRegistration(true);
      // Scroll to registration section after component mounts
      setTimeout(() => {
        const registrationElement = document.getElementById('registration-section');
        if (registrationElement) {
          registrationElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location.hash]);

  const handleBack = () => {
    navigate('/');
  };

  const handleTabClick = (tab: string) => {
    switch (tab) {
      case 'dashboard':
        navigate('/');
        break;
      case 'events':
        navigate('/?tab=events');
        break;
      case 'my-events':
        navigate('/?tab=my-events');
        break;
      case 'profile':
        navigate('/?tab=profile');
        break;
      default:
        setActiveTab(tab);
    }
  };

  const handleContactOrganizer = async () => {
    try {
      // Get event data to find organizer_id
      const { data: event, error } = await supabase
        .from('events')
        .select('organizer_id')
        .eq('id', tournamentId)
        .single();
      
      if (error || !event) {
        toast({
          title: "Error",
          description: "No se pudo encontrar el organizador",
          variant: "destructive"
        });
        return;
      }
      
      // Create or get chat with organizer
      const chatId = await createOrGetChat(event.organizer_id);
      
      // Navigate to chat with real chat ID
      navigate(`/chat/${chatId}`);
    } catch (error) {
      console.error('Error creating chat:', error);
      toast({
        title: "Error",
        description: "No se pudo iniciar el chat",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1b3521] via-[#31633f] to-[#6da079]">
      <PlayerNav 
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onTabClick={handleTabClick}
      />
      
      <main className={`container mx-auto ${isMobile ? 'px-2 pt-2 pb-20' : 'px-4 pt-4 pb-20'}`}>
        <TournamentDetails 
          tournamentId={tournamentId}
          onBack={handleBack}
          showRegistration={showRegistration}
          onContactOrganizer={handleContactOrganizer}
        />
      </main>
    </div>
  );
};

export default TournamentDetailsPage;
